/**
 * Sell-side recommendation tier from a composite score. Used by every UI
 * surface that displays a composite — IdeaCard, StockDetail, StockTooltip,
 * HoldingsList, leaderboard, etc. Single source of truth; do not inline.
 *
 * Vocabulary (Steve's directive 2026-04-30): STRONG BUY / BUY / HOLD / SELL.
 * Thresholds align with the Research Desk's hedge-fund qualification bar in
 * lib/research/state-machine.ts: composite ≥3.0 = qualifies as new idea
 * (STRONG BUY); ≥1.5 = solidly above average (BUY); ≥0 = positive but weak
 * (HOLD); <0 = model has flipped negative (SELL).
 */

export interface CompositeTier {
  label: string;
  /** Hex value for inline backgroundColor / stroke. Tailwind JIT cannot pick
   *  up dynamically-built bg-${...} class names, so we use raw hex. */
  hex: string;
  /** Class name for text colors (statically used elsewhere, JIT-safe). */
  textClass: string;
  /** Pre-formed Tailwind background tint at 15% opacity, JIT-safe. */
  bgClass: string;
  /** Pre-formed Tailwind border at 40% opacity, JIT-safe. */
  borderClass: string;
}

export function compositeTier(c: number | null | undefined): CompositeTier {
  if (c == null || !Number.isFinite(c)) {
    return { label: '—', hex: '#64748b', textClass: 'text-zinc-500', bgClass: 'bg-zinc-700/15', borderClass: 'border-zinc-600/40' };
  }
  if (c >= 3)   return { label: 'strong buy', hex: '#4ade80', textClass: 'text-emerald-400', bgClass: 'bg-emerald-500/15', borderClass: 'border-emerald-500/40' };
  if (c >= 1.5) return { label: 'buy',        hex: '#86efac', textClass: 'text-emerald-300', bgClass: 'bg-emerald-500/15', borderClass: 'border-emerald-500/40' };
  if (c >= 0)   return { label: 'hold',       hex: '#fcd34d', textClass: 'text-amber-300',   bgClass: 'bg-amber-500/15',   borderClass: 'border-amber-500/40' };
  return            { label: 'sell',          hex: '#f87171', textClass: 'text-red-400',     bgClass: 'bg-red-500/15',     borderClass: 'border-red-500/40' };
}
